/**
 * Issue #241 — per-order Kalshi cancel lifecycle (pure).
 *
 * A successful DELETE is not evidence the order is gone. The relay's Open
 * Orders read is org-snapshot backed, so the refresh fired straight after a
 * cancel can still return the pre-cancel book; the row would briefly re-enable
 * and a second click would send a duplicate DELETE that fails misleadingly
 * *after* the first cancel actually worked (Grim, 2026-07-31).
 *
 * So the fence lives here: an order stays pending from the moment its cancel is
 * requested until a successfully parsed authoritative snapshot no longer lists
 * it — with a bounded 5,000 ms escape so a stalled sync can't disable the
 * control forever.
 *
 * Deliberately free of timers, React, Zustand, network and order parsing: the
 * store owns the clock and the transport, this module owns the transitions.
 * Every function returns the input map by identity when nothing changed, so a
 * quiet poll cannot churn `kalshiCancelStates` and re-render every order row.
 */

/** Bounded escape from a stalled Open Orders synchronization. */
export const KALSHI_CANCEL_SYNC_TIMEOUT_MS = 5_000;

/** Truthful copy: the cancel succeeded, the *confirmation* is what's missing.
 *  Derived from the constant above so the two can never drift apart. */
export const KALSHI_CANCEL_SYNC_TIMEOUT_MESSAGE =
  `Cancel succeeded, but Open Orders still shows this order after ${
    KALSHI_CANCEL_SYNC_TIMEOUT_MS / 1_000
  } seconds. Verify synchronization before retrying.`;

export type KalshiCancelPhase =
  /** DELETE is in flight. */
  | 'requesting'
  /** DELETE succeeded; waiting for the ID to leave the authoritative list. */
  | 'awaiting-removal'
  /** Waited past the timeout. Retryable — NOT proof the order survived. */
  | 'timed-out';

export interface KalshiCancelState {
  phase: KalshiCancelPhase;
  /** Phase start. Restamped on DELETE success — that instant starts the window. */
  startedAtMs: number;
  /** Set only on 'timed-out'. */
  message?: string;
}

export type KalshiCancelStates = Readonly<Partial<Record<string, KalshiCancelState>>>;

/**
 * Per-ID outcome of a batched cancel (#272). Lives here, beside the state
 * machine it reports on, so `accountStore` does not have to import the bulk
 * executor to describe its own return type (`bulkOrderCancel` re-exports it as
 * part of the executor's public surface).
 *
 * `submitted` is deliberately not called `cancelled`: a 2xx DELETE is a request
 * the venue accepted, and the order is only proven gone once an authoritative
 * Open Orders snapshot stops listing it.
 */
export interface KalshiCancelBatchResult {
  orderId: string;
  status: 'submitted' | 'failed' | 'already-pending';
  error: string | null;
}

/** The row's Cancel/Edit controls are disabled exactly while this is true.
 *  'timed-out' is deliberately NOT pending: the trader gets the control back. */
export function isKalshiCancelPending(state: KalshiCancelState | undefined): boolean {
  return state?.phase === 'requesting' || state?.phase === 'awaiting-removal';
}

/** Fence an ID as its DELETE goes out. Also the retry entry point — it drops any
 *  previous timed-out state (and its message) for the same ID. */
export function beginKalshiCancel(
  states: KalshiCancelStates,
  orderId: string,
  nowMs: number,
): KalshiCancelStates {
  return { ...states, [orderId]: { phase: 'requesting', startedAtMs: nowMs } };
}

/** DELETE resolved 2xx. Only promotes an attempt that is still 'requesting': if
 *  an authoritative snapshot (or a disconnect) cleared the ID while the request
 *  was in flight, a late success must not resurrect the fence. */
export function markKalshiCancelAwaitingRemoval(
  states: KalshiCancelStates,
  orderId: string,
  nowMs: number,
): KalshiCancelStates {
  if (states[orderId]?.phase !== 'requesting') return states;
  return { ...states, [orderId]: { phase: 'awaiting-removal', startedAtMs: nowMs } };
}

/** The 5,000 ms window elapsed with the ID still listed. Only an entry that is
 *  actually awaiting removal can time out — a stale timer must not promote the
 *  fresh 'requesting' state of a later retry. */
export function markKalshiCancelTimedOut(
  states: KalshiCancelStates,
  orderId: string,
): KalshiCancelStates {
  const current = states[orderId];
  if (current?.phase !== 'awaiting-removal') return states;
  return {
    ...states,
    [orderId]: { ...current, phase: 'timed-out', message: KALSHI_CANCEL_SYNC_TIMEOUT_MESSAGE },
  };
}

/** Drop one ID's state (DELETE rejected, or disconnect). */
export function clearKalshiCancel(
  states: KalshiCancelStates,
  orderId: string,
): KalshiCancelStates {
  if (states[orderId] === undefined) return states;
  const next = { ...states };
  delete next[orderId];
  return next;
}

/**
 * The authoritative step. Given the order-ID set of a *successfully parsed*
 * snapshot, drop every fenced ID the venue no longer lists — that disappearance
 * is the only real cancellation confirmation. IDs still present stay fenced,
 * which is what keeps a stale post-cancel refresh from re-enabling the row.
 *
 * Call this ONLY for a snapshot that passed `parseOrdersSnapshot`. A failed or
 * malformed read is not evidence of anything and must not reach here.
 */
export function reconcileKalshiCancels(
  states: KalshiCancelStates,
  authoritativeOrderIds: Iterable<string>,
): KalshiCancelStates {
  const fenced = Object.keys(states);
  if (fenced.length === 0) return states; // the overwhelmingly common poll
  const live = new Set(authoritativeOrderIds);
  const removed = fenced.filter((orderId) => !live.has(orderId));
  if (removed.length === 0) return states;
  const next = { ...states };
  for (const orderId of removed) delete next[orderId];
  return next;
}

// ── #968 confirmed-cancel tombstones ────────────────────────────────────────
//
// `reconcileKalshiCancels` retires a fence on the FIRST snapshot that omits the
// id. Nothing then stops an OLDER snapshot that still lists it (a slow HTTP
// body behind a newer push frame, or a relay poll that was already in flight
// when the DELETE landed) from re-admitting the row as live, which reads as
// shade, vanish, reappear, vanish. A tombstone remembers the confirmation.
//
// Ordering evidence is the relay's `ordersAsOf` (the relay time the order list
// was read). Only a snapshot demonstrably newer than the confirming one brings
// the id back, and then the cancel is surfaced as unconfirmed rather than
// silently live. Without evidence on either side the tombstone hides the row
// for at most KALSHI_CANCEL_TOMBSTONE_MS, so a genuinely live order is never
// hidden for long. Bounded in time and size; the store clears it on disconnect.

/** Longest a tombstone can hide a listed row without ordering evidence. Sized
 *  to the relay's org-snapshot serve window, the oldest book it can hand out. */
export const KALSHI_CANCEL_TOMBSTONE_MS = 15_000;

/** Hard cap; the oldest tombstones go first. */
export const KALSHI_CANCEL_TOMBSTONE_MAX = 500;

export const KALSHI_CANCEL_RELISTED_MESSAGE =
  'Cancel was confirmed, but Open Orders lists this order again. Verify before retrying.';

/** #971: the trader EDITED this order; never tell them they cancelled it. */
export const KALSHI_EDIT_RELISTED_MESSAGE =
  'Open Orders lists the original order again after an edit attempt. Verify before retrying.';

/** What retired the id: a fenced cancel, or an edit's cancel-first leg. */
export type KalshiTombstoneOrigin = 'cancel' | 'edit';

export interface KalshiCancelTombstone {
  /** `ordersAsOf` of the snapshot that confirmed absence; null when unknown. */
  asOf: number | null;
  /** Local receipt time of that confirmation — the TTL clock only. */
  confirmedAtMs: number;
  origin: KalshiTombstoneOrigin;
}

export type KalshiCancelTombstones = ReadonlyMap<string, KalshiCancelTombstone>;

/** Whether `tomb` still hides its id in a snapshot stamped `snapshotAsOf`:
 *  it has not expired, and the snapshot is not demonstrably newer than the
 *  confirmation. The one rule every lane applies (#971). */
export function kalshiTombstoneHides(
  tomb: KalshiCancelTombstone,
  snapshotAsOf: number | null,
  nowMs: number,
): boolean {
  const expired = nowMs - tomb.confirmedAtMs > KALSHI_CANCEL_TOMBSTONE_MS;
  const newer = snapshotAsOf !== null && tomb.asOf !== null && snapshotAsOf > tomb.asOf;
  return !expired && !newer;
}

/** Record confirmations for `orderIds`. Returns the input by identity when
 *  there is nothing to add. */
export function addKalshiCancelTombstones(
  tombstones: KalshiCancelTombstones,
  orderIds: readonly string[],
  asOf: number | null,
  nowMs: number,
  origin: KalshiTombstoneOrigin = 'cancel',
): KalshiCancelTombstones {
  if (orderIds.length === 0) return tombstones;
  const next = new Map(tombstones);
  for (const orderId of orderIds) {
    next.delete(orderId); // re-insert so Map order stays oldest-first
    next.set(orderId, { asOf, confirmedAtMs: nowMs, origin });
  }
  while (next.size > KALSHI_CANCEL_TOMBSTONE_MAX) {
    const oldest = next.keys().next().value as string;
    next.delete(oldest);
  }
  return next;
}

export interface KalshiTombstoneOutcome<O extends { orderId: string }> {
  /** The snapshot with every still-tombstoned row removed. */
  orders: O[];
  tombstones: KalshiCancelTombstones;
  /** Ids a demonstrably newer snapshot (or an expired tombstone) lists again. */
  relisted: KalshiRelisted[];
}

/** An id a snapshot lists again after its tombstone stood down. */
export interface KalshiRelisted {
  orderId: string;
  origin: KalshiTombstoneOrigin;
}

/**
 * Filter a validated snapshot through the tombstones. `snapshotAsOf` is that
 * snapshot's `ordersAsOf`, or null when the relay did not stamp it.
 */
export function applyKalshiCancelTombstones<O extends { orderId: string }>(
  tombstones: KalshiCancelTombstones,
  orders: O[],
  snapshotAsOf: number | null,
  nowMs: number,
): KalshiTombstoneOutcome<O> {
  if (tombstones.size === 0) return { orders, tombstones, relisted: [] };
  let next: Map<string, KalshiCancelTombstone> | null = null;
  const drop = (orderId: string) => {
    next ??= new Map(tombstones);
    next.delete(orderId);
  };
  const relisted: KalshiRelisted[] = [];
  const hidden = new Set<string>();
  const listed = new Set<string>();
  for (const order of orders) {
    const tomb = tombstones.get(order.orderId);
    if (!tomb) continue;
    listed.add(order.orderId);
    if (kalshiTombstoneHides(tomb, snapshotAsOf, nowMs)) {
      hidden.add(order.orderId);
    } else {
      drop(order.orderId);
      relisted.push({ orderId: order.orderId, origin: tomb.origin });
    }
  }
  // Unlisted tombstones age out on the same clock.
  for (const [orderId, tomb] of tombstones) {
    if (!listed.has(orderId) && nowMs - tomb.confirmedAtMs > KALSHI_CANCEL_TOMBSTONE_MS) drop(orderId);
  }
  return {
    orders: hidden.size === 0 ? orders : orders.filter((order) => !hidden.has(order.orderId)),
    tombstones: next ?? tombstones,
    relisted,
  };
}

/** A relisted id gets its control back with truthful copy — never silently live. */
export function markKalshiCancelRelisted(
  states: KalshiCancelStates,
  relisted: readonly KalshiRelisted[],
  nowMs: number,
): KalshiCancelStates {
  if (relisted.length === 0) return states;
  const next = { ...states };
  for (const { orderId, origin } of relisted) {
    const message = origin === 'edit' ? KALSHI_EDIT_RELISTED_MESSAGE : KALSHI_CANCEL_RELISTED_MESSAGE;
    next[orderId] = { phase: 'timed-out', startedAtMs: nowMs, message };
  }
  return next;
}

// ── #968/#971 order-list vintage ────────────────────────────────────────────

/** A relay clock step (restart, NTP) must not wedge a lane: past this
 *  distance an "older" stamp is treated as a rebase, not a stale read. The
 *  binding bound is the slowest HTTP body a lane still applies: a body whose
 *  read ran longer than this can outrun it and apply as a "rebase". Keep it
 *  >= the overlay's SNAPSHOT_READ_TIMEOUT_MS (useUserKalshiPOS). */
export const ORDERS_AS_OF_REBASE_MS = 10_000;

/**
 * Orders one lane's order-list snapshots by the relay's `ordersAsOf`. Two
 * transports carry the same book, and a slow one can deliver an older list
 * after a newer one was applied; that list is not a newer view of anything.
 * Each lane owns its own gate: the baseline is "newest list THIS lane
 * applied", so one lane's read never starves another lane's frames.
 */
export class OrdersVintageGate {
  private applied: { scope: string | null; asOf: number } | null = null;

  /** True when a snapshot stamped `asOf` is older than the newest one applied
   *  for the same `scope`. Records the stamp when it is not. Unstamped
   *  snapshots are never ordered (last writer wins). */
  superseded(scope: string | null, asOf: number | null): boolean {
    if (asOf === null) return false;
    const applied = this.applied;
    if (applied && applied.scope === scope && asOf < applied.asOf
      && applied.asOf - asOf <= ORDERS_AS_OF_REBASE_MS) {
      return true;
    }
    this.applied = { scope, asOf };
    return false;
  }

  /** Forget the baseline (disconnect, reconnect, logout). */
  reset(): void {
    this.applied = null;
  }
}
